import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject, ReplaySubject, Subject, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IPaginatedData, IResponse } from '../interfaces';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { LanguageService } from './language.service';

@Injectable({ providedIn: 'root' })
export class StorageService {
  constructor(
    private readonly http: HttpClient,
    private readonly languageService: LanguageService,
  ) { }

  // Generic SWR cache for paginated product lists
  private productsCache = new Map<string, { obs$: Observable<IPaginatedData<any>> | null; ts: number; subject: ReplaySubject<IPaginatedData<any>> }>();
  private readonly PROD_CACHE_MS = 5 * 60 * 1000; // 5 minutes - longer cache for better performance
  private readonly PROD_LS_PREFIX = 'products_cache_v1_';

  // Individual product cache with shorter TTL for fresher data
  private productCache = new Map<string, { obs$: Observable<any> | null; ts: number; subject: ReplaySubject<any> }>();
  private readonly SINGLE_PROD_CACHE_MS = 2 * 60 * 1000; // 2 minutes for individual products
  private readonly SINGLE_PROD_LS_PREFIX = 'product_cache_v1_';

  private makeProductsKey(url: string, body: any): string {
    return `${url}__${this.languageService.currentLanguage}__${JSON.stringify(body || {})}`;
  }

  private safeGetLS(key: string): string | null { try { return localStorage.getItem(key); } catch { return null; } }
  private safeSetLS(key: string, val: string): void { try { localStorage.setItem(key, val); } catch { /* ignore */ } }
  private safeRmLS(key: string): void { try { localStorage.removeItem(key); } catch { /* ignore */ } }

  // Public method to clear products cache when needed
  clearProductsCache(): void {
    // Reset timestamps instead of clearing subjects to maintain subscriptions
    this.productsCache.forEach(entry => {
      entry.ts = 0;
      entry.obs$ = null;
    });
    this.productCache.forEach(entry => {
      entry.ts = 0;
      entry.obs$ = null;
    });
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.PROD_LS_PREFIX) || key.startsWith(this.SINGLE_PROD_LS_PREFIX)) {
        this.safeRmLS(key);
      }
    });
  }

  // Clear cache specifically for category/subcategory queries to ensure fresh data
  clearCategoryCache(): void {
    // Clear in-memory cache for products
    this.productsCache.forEach((entry, key) => {
      // Check if this cache key contains category or sub_category
      if (key.includes('category') || key.includes('sub_category')) {
        entry.ts = 0;
        entry.obs$ = null;
      }
    });

    // Clear localStorage cache for category-related queries
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.PROD_LS_PREFIX)) {
        const cacheContent = this.safeGetLS(key);
        if (cacheContent) {
          try {
            const parsed = JSON.parse(cacheContent);
            // Check if this cache is for a category query
            if (parsed && parsed.data && this.isCategoryQuery(parsed)) {
              this.safeRmLS(key);
              // Also remove timestamp key
              const tsKey = key + '_ts';
              this.safeRmLS(tsKey);
            }
          } catch {
            // If parse fails, remove the key anyway
            this.safeRmLS(key);
            const tsKey = key + '_ts';
            this.safeRmLS(tsKey);
          }
        }
      }
    });
  }

  // Helper to determine if cache is for category query
  private isCategoryQuery(cacheData: any): boolean {
    // Check if the original query had category or sub_category filters
    // The query is stored in the cache key, not in the data
    return false; // Safe default - we clear more broadly for category changes
  }

  /* Product Categories */

  getProductCategories(
    page: number = 1,
    limit: number = 10,
    query: any = {}
  ): Observable<IPaginatedData<any>> {
    const finalRoute = `${environment.baseUrl}/products__category_product/find?page=${page}&limit=${limit}`;
    return this.http.post<IPaginatedData<any>>(finalRoute, query);
  }

  getProductCategoriesByLevel(
    page: number = 1,
    limit: number = 10,
    query: any = {}
  ): Observable<IPaginatedData<any>> {
    const finalRoute = `${environment.baseUrl}/products__category_product/categories-by-level?page=${page}&limit=${limit}`;
    return this.http.post<IPaginatedData<any>>(finalRoute, query);
  }

  getProductCategory(id: string): Observable<IResponse<any>> {
    const finalRoute = `${environment.baseUrl}/products__category_product/${id}`;
    return this.http.get<IResponse<any>>(finalRoute);
  }

  getColors(query: any): Observable<string[]> {
    const finalRoute = `${environment.baseUrl}/products__product_child/get-colors`;
    return this.http.post<string[]>(finalRoute, query);
  }

  /* Products */

  getProducts(
    page: number = 1,
    limit: number = 10,
    preOrder: string = '',
    query: any = {},
    bypassCache: boolean = false,
    propagateErrors: boolean = false
  ): Observable<IPaginatedData<any>> {
    const finalRoute = `${environment.baseUrl}/products__product_child/find?page=${page}&limit=${limit}&preOrder=${preOrder}`;

    const cacheKey = this.makeProductsKey(finalRoute, query);
    const now = Date.now();

    // Always fetch fresh data if bypassCache is true
    if (bypassCache) {
      // Clear existing cache for this key
      this.productsCache.delete(cacheKey);
      const lsKey = this.PROD_LS_PREFIX + cacheKey;
      const lsTsKey = lsKey + '_ts';
      this.safeRmLS(lsKey);
      this.safeRmLS(lsTsKey);
    }

    // Emit from localStorage immediately if fresh
    const lsKey = this.PROD_LS_PREFIX + cacheKey;
    const lsTsKey = lsKey + '_ts';
    const lsJson = bypassCache ? null : this.safeGetLS(lsKey);
    const lsTsStr = bypassCache ? null : this.safeGetLS(lsTsKey);
    const lsTs = lsTsStr ? Number(lsTsStr) : 0;

    if (!this.productsCache.has(cacheKey)) {
      this.productsCache.set(cacheKey, {
        subject: new ReplaySubject<IPaginatedData<any>>(1),
        obs$: null,
        ts: 0,
      });
    }

    const entry = this.productsCache.get(cacheKey)!;

    // Only use cache if not bypassed and cache is fresh
    if (!bypassCache && lsJson && lsTs && now - lsTs < this.PROD_CACHE_MS) {
      try {
        const parsed = JSON.parse(lsJson);
        // Only reject cache if it's completely invalid (missing data array)
        if (parsed && Array.isArray(parsed.data)) {
          entry.subject.next(parsed);
          entry.ts = lsTs; // Update in-memory timestamp to match cache
          return entry.subject.asObservable();
        } else {
          // Clear invalid cache only if structure is completely wrong
          this.safeRmLS(lsKey);
          this.safeRmLS(lsTsKey);
        }
      } catch { /* ignore */ }
    }

    if (!bypassCache && entry.obs$ && now - entry.ts < this.PROD_CACHE_MS) {
      // Return hot subject while in-memory cache valid
      entry.obs$.subscribe({ next: (res) => entry.subject.next(res) });
      return propagateErrors ? entry.obs$ : entry.subject.asObservable();
    }

    // Fetch, update caches, and emit
    entry.obs$ = this.http.post<IPaginatedData<any>>(finalRoute, query).pipe(
      tap((res) => {
        entry.ts = Date.now();
        this.safeSetLS(lsKey, JSON.stringify(res));
        this.safeSetLS(lsTsKey, String(entry.ts));
        entry.subject.next(res);
      }),
      catchError((err) => {
        entry.ts = 0;
        this.safeRmLS(lsKey);
        this.safeRmLS(lsTsKey);
        return propagateErrors
          ? throwError(() => err)
          : of({ data: [], meta: undefined } as any);
      }),
      shareReplay(1)
    );

    if (propagateErrors) {
      return entry.obs$;
    }

    // Push fresh result into subject
    entry.obs$.subscribe({ next: (res) => entry.subject.next(res) });
    return entry.subject.asObservable();
  }

  getProduct(id: string): Observable<any> {
    const finalRoute = `${environment.baseUrl}/products__product_child/${id}?lite=true`;
    // Cache disabled as per request
    return this.http.get<any>(finalRoute);
  }

  getVariantsForFilters(query: any): Observable<any[]> {
    const finalRoute = `${environment.baseUrl}/products__product_child/get-variants`;
    return this.http.post<any[]>(finalRoute, query);
  }

  /* Tags */

  getTags(
    page: number = 1,
    limit: number = 100,
    query: any = {}
  ): Observable<IPaginatedData<any>> {
    const finalRoute = `${environment.baseUrl}/products__tags/find?page=${page}&limit=${limit}`;
    return this.http.post<IPaginatedData<any>>(finalRoute, query);
  }

  /* Standards */

  getStandards(
    page: number = 1,
    limit: number = 100,
    query: any = {}
  ): Observable<IPaginatedData<any>> {
    const finalRoute = `${environment.baseUrl}/products__standard/find?page=${page}&limit=${limit}`;
    return this.http.post<IPaginatedData<any>>(finalRoute, query);
  }

  /* Key Features */

  findKeyFeatures(
    page: number = 1,
    limit: number = 100,
    query: any = {}
  ): Observable<IPaginatedData<any>> {
    const finalRoute = `${environment.baseUrl}/products__key_features/find?page=${page}&limit=${limit}`;
    return this.http.post<IPaginatedData<any>>(finalRoute, query);
  }

  getProductsWithColorFilter(
    page: number = 1,
    limit: number = 10,
    preOrder: string = '',
    query: any = {}
  ): Observable<IPaginatedData<any>> {
    const finalRoute = `${environment.baseUrl}/products__product_child/findAllWithColorFilter?page=${page}&limit=${limit}&preOrder=${preOrder}`;
    return this.http.post<IPaginatedData<any>>(finalRoute, query);
  }

  /* Manufacturers */

  getManufacturers(): Observable<string[]> {
    const finalRoute = `${environment.baseUrl}/products__product_child/get-producers`;
    return this.http.get<string[]>(finalRoute);
  }

  getProductFilterFacets(query: Record<string, any>): Observable<{
    manufacturers: Array<{ name: string; count: number }>;
    tags: Array<{ id: number; label: string; count: number }>;
    standards: Array<{ id: number; title: string; documentUrl?: string; count: number }>;
    keyFeatures: Array<{ id: number; title: string; count: number }>;
    variants: Array<{ variantId: string; value: string; count: number }>;
    colors: Array<{ name: string; count: number }>;
    priceRange: { max: number | null };
  }> {
    const finalRoute = `${environment.baseUrl}/products__product_child/get-filter-facets`;
    return this.http.post<{
      manufacturers: Array<{ name: string; count: number }>;
      tags: Array<{ id: number; label: string; count: number }>;
      standards: Array<{ id: number; title: string; documentUrl?: string; count: number }>;
      keyFeatures: Array<{ id: number; title: string; count: number }>;
      variants: Array<{ variantId: string; value: string; count: number }>;
      colors: Array<{ name: string; count: number }>;
      priceRange: { max: number | null };
    }>(finalRoute, query);
  }
}
